//dependencies
import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { ButtonStandard, Pagination, SortChangedEvent, Table } from "@performyard/py-components";

// components
import { SideNavigationLink } from "../../../../../components/SideNavigation";
import getReviewCycleTableColsRefresh from "./components/ReviewCycleTableColsRefresh";
import ReviewCyclesFilters from "./components/ReviewCyclesFilters";
import ReviewCycleReadableFiltersList from "./components/ReviewCyclesReadableFilters";

// types
import type { ReviewCycleListItem } from "./types";

// hooks
import { useReviewCycles } from "../../../../../api/hooks";
import { useFeatureFlagQuery } from "../../../../../hooks/useFeatureFlag";

// utils
import { appendCurrentLocationAsFrom } from "../../../../../utils/createReviewCycleBreadcrumbs";

//template
import InfoPageTemplate from "../../../../../pageTemplates/InfoPageTemplate";
import StandardToolbarActionRow from "../../../../../pageTemplates/InfoPageTemplate/StandardToolbarActionRow";
import useInfoPageTemplateQueryState from "../../../../../pageTemplates/InfoPageTemplate/useInfoPageTemplateQueryState";
// utils
import { CompanyReviewsNavigationLinks, REVIEWS_TABLE_LIMIT } from "../../constants";
import { getQueryParamsFromFilters, mapSortField } from "./refresh-utils";

type ReviewCyclesRefreshProps = {
	navLinks: SideNavigationLink[];
};

const ReviewCyclesRefresh = ({ navLinks }: ReviewCyclesRefreshProps) => {
	const { page } = useParams<{ page: string }>();
	const navigate = useNavigate();

	const {
		pageNumber,
		sort,
		filters,
		setPageNumber,
		setSort,
		setFilters,
		searchValue,
		debouncedSearchValue,
		setDebouncedSearch,
	} = useInfoPageTemplateQueryState({
		defaultSortName: "startDate",
		defaultSortDirection: "desc",
		defaultPageNumber: 1,
	});
	const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState(false);

	const queryParams = getQueryParamsFromFilters(filters, debouncedSearchValue);

	const { data, isLoading } = useReviewCycles({
		...queryParams,
		sortName: mapSortField(sort.name),
		sortDirection: sort.direction as "asc" | "desc",
		skip: (pageNumber - 1) * REVIEWS_TABLE_LIMIT,
		limit: REVIEWS_TABLE_LIMIT,
	});

	const { value: featureFlag } = useFeatureFlagQuery("autoflow", false);

	const reviewCycles = data?.data || [];
	const totalCycles = data?.meta?.pagination?.total || 0;

	const additionalFiltersCount = Object.keys(filters).length;

	const columnDefs = useMemo(() => getReviewCycleTableColsRefresh(), []);

	const onSortChanged = (event: SortChangedEvent<ReviewCycleListItem>) => {
		const columnState = event.api.getColumnState();
		const sortedColumn = columnState.find((col) => col.sort);
		if (sortedColumn?.colId) {
			setSort({
				name: sortedColumn.colId,
				direction: sortedColumn.sort as "asc" | "desc",
			});
		}
	};

	const toolbarActionRow = (
		<StandardToolbarActionRow
			searchInput={searchValue}
			onSearchInputChange={(value) => setDebouncedSearch(value)}
			onFilterButtonPress={() => {
				setIsFiltersPanelOpen(true);
			}}
			activeFiltersCount={additionalFiltersCount}
			searchPlaceholder="Search reviews cycles..."
		/>
	);

	const toolbarInformationRow = (
		<ReviewCycleReadableFiltersList filters={filters} setFilters={(filters) => setFilters({ ...filters })} />
	);

	const drawer = (
		<ReviewCyclesFilters
			isOpen={isFiltersPanelOpen}
			onClose={() => setIsFiltersPanelOpen(false)}
			filters={filters}
			onApplyFilters={(newFilters) => {
				setFilters({ ...newFilters });
			}}
			automationEnabled={featureFlag}
		/>
	);

	const actionButtons = [
		<ButtonStandard
			key="create-review-cycle-button"
			color="green"
			onClick={() => navigate(appendCurrentLocationAsFrom("/review-cycle/create/"))}
		>
			New Review Cycle
		</ButtonStandard>,
	];

	const mainContent = (
		<div>
			<Table<ReviewCycleListItem>
				autoHeight
				rowData={reviewCycles}
				columnDefs={columnDefs}
				loading={isLoading}
				getRowId={(params) => params.data._id}
				onSortChanged={onSortChanged}
				emptyStateText="No Review Cycles Found"
				initialState={{
					sort: {
						sortModel: sort.name ? [{ colId: sort.name, sort: sort.direction as "asc" | "desc" }] : [],
					},
				}}
				animateRows={false}
			/>
			{totalCycles > 0 && (
				<div className="mx-auto mt-5 flex w-full max-w-[400px] justify-center">
					<Pagination
						currentPage={pageNumber}
						pageCount={Math.ceil(totalCycles / REVIEWS_TABLE_LIMIT)}
						onPageChange={(pageNumber: number) => setPageNumber(pageNumber)}
					/>
				</div>
			)}
		</div>
	);

	return (
		<InfoPageTemplate
			toolbarActionRow={toolbarActionRow}
			toolbarInformationRow={toolbarInformationRow}
			actionButtons={actionButtons}
			mainContent={mainContent}
			drawer={drawer}
			navigationLinks={navLinks || CompanyReviewsNavigationLinks}
			activeNavigationLinkKey={page}
		/>
	);
};

export default ReviewCyclesRefresh;
